import { useCalendarContext } from "@/context/calendar";
import { TableFilter } from "@/types/utils";
import { getAuthenticatedPages } from "@/utils/helpers";
import storage, { ObjectKeys, StringKeys } from "@/utils/storage";
import constant from "@/utils/constant";
import { addDays } from "date-fns";
import { useEffect } from "react";

const useDataPicker = () => {
  const { setDate, date, reset, selectedRange } = useCalendarContext();
  const pages = getAuthenticatedPages();
  const storageKey: StringKeys | ObjectKeys = pages.dashboard
    ? "dashboard_filter"
    : pages.orders
      ? "orders_filter"
      : "brand_orders_filter";
  const filters = storage.getItem(storageKey) as TableFilter | null;
  const storageSelectedRange = filters?.selectedRange;

  const onTodayClick = () => {
    const today = new Date();
    setDate({ from: today, to: today }, storageKey, "today");
  };

  const onYesterdayClick = () => {
    const yesterday = addDays(new Date(), -1);
    setDate({ from: yesterday, to: yesterday }, storageKey, "yesterday");
  };

  const onThisWeekClick = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diffToMonday = (dayOfWeek + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - diffToMonday);
    monday.setHours(0, 0, 0, 0);

    setDate({ from: monday, to: today }, storageKey, "thisWeek");
  };

  const onThisMonthClick = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    setDate({ from: firstDayOfMonth, to: today }, storageKey, "thisMonth");
  };

  const onLastMonthClick = () => {
    const today = new Date();
    const firstDayOfLastMonth = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1
    );
    const lastDayOfLastMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      0
    );
    setDate(
      { from: firstDayOfLastMonth, to: lastDayOfLastMonth },
      storageKey,
      "lastMonth"
    );
  };

  const getButtonVariant = (range: string) =>
    selectedRange === range || storageSelectedRange === range
      ? "default"
      : "outline";
      
  useEffect(() => {
    const storedDate = storage.getItem(storageKey);

    if (constant.filterStorageObjectKeys.includes(storageKey)) {
      if (
        storedDate &&
        typeof storedDate === "object" &&
        "dates" in storedDate
      ) {
        const { dates } = storedDate as TableFilter;
        if (dates) {
          setDate(dates, storageKey, storageSelectedRange);
        }
      }
    } else {
      if (typeof storedDate === "string") {
        setDate(JSON.parse(storedDate), storageKey, storageSelectedRange);
      }
    }

    return () => {
      reset();
    };
  }, []);

  return {
    date,
    setDate,
    onTodayClick,
    onYesterdayClick,
    onThisWeekClick,
    onThisMonthClick,
    onLastMonthClick,
    storageKey,
    getButtonVariant,
  };
};

export { useDataPicker };
